import React, { useState, useEffect, useRef } from "react";
import { Plus, Minus } from "lucide-react";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import {
  ThinDropdownWithSearch,
  ThinTextField,
} from "../../../components/common/GeneralComponent";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchData } from "../../../utils/functions";
import { toast } from "react-toastify";
import ApplicationsSection from "./ApplicationsSection";
import TotalAllocatedSection from "./TotalAllocatedSection";
import StatusSection from "./StatusSection";
import SummaryTableSection from "./SummaryTableSection";
import MilestoneStatusSection from "./MilestoneStatusSection";
import BusinessTypeSection from "./BusinessTypeSection";
import BusinessSectorSection from "./BusinessSectorSection";
import GenderSection from "./GenderSection";
import NoData, { hasData } from "../../common/NoData";
import ActiveFilterChips from "../../common/ActiveFilterChips";
import { useSelector } from "react-redux";

const BusinessGrantsDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];
  const isAdmin = loggedInUser?.user_role_name === "Super Admin";
  // Permission-based visibility
  const canViewSME = permissions.includes("dashboard-business-grant-sme");
  const canViewMicro = permissions.includes("dashboard-business-grant-micro");
  const canViewCorporate = permissions.includes(
    "dashboard-business-grant-corporate"
  );
  const canViewSocialEnterprise = permissions.includes(
    "dashboard-business-grant-social-enterprise"
  );
  const canViewSedfEnterprise = permissions.includes(
    "dashboard-business-grant-sedf-enterprise"
  );

  const [activeSubTab, setActiveSubTab] = useState(
    canViewSME
      ? "sme"
      : canViewCorporate
      ? "corporate"
      : canViewSocialEnterprise
      ? "social-enterprise"
      : canViewSedfEnterprise
      ? "sedf-enterprise"
      : "micro"
  );
  const [dashboardData, setDashboardData] = useState(null);
  const [filtersData, setFiltersData] = useState({
    districts: [],
    cycles: [],
    smeDateFields: [],
    microDateFields: [],
    conditions: [],
    businessEntities: [],
    businessSectors: [],
    genders: [],
    overallStatuses: [],
    qualificationStatuses: [],
  });
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [filters, setFilters] = useState({
    district_id: "",
    cycle_id: "",
    dateField: "",
    condition: "",
    dateValue: "",
    startDate: "",
    endDate: "",
    qualification_status: "",
    overall_status: "",
    business_entity: "",
    gender: "",
    business_sector: "",
    ips_group: "",
  });
  const [loading, setLoading] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [ipsGroups] = useState([
    { label: "QAIP", value: "QAIP" },
    { label: "NRSP", value: "NRSP" },
    { label: "SRSO", value: "SRSO" },
    { label: "HANDS", value: "HANDS" },
    { label: "SAFCO", value: "SAFCO" },
  ]);

  const latestRequestRef = useRef(0);

  useEffect(() => {
    fetchFiltersData();
  }, []);

  // Fetch data when tab changes
  useEffect(() => {
    if (filtersApplied) {
      fetchDashboardData({}, true);
    } else {
      // Otherwise fetch fresh data for new tab
      setDashboardData(null);
      fetchDashboardData({}, false);
    }
  }, [activeSubTab]);

  const fetchFiltersData = async () => {
    try {
      const response = await fetchData(
        "business-grants/filters",
        "GET",
        null,
        {},
        {},
        navigate,
        "/business-grants",
        dispatch
      );

      if (response.success) {
        setFiltersData(response.data);
      }
    } catch (error) {
      console.error("Error fetching filters data:", error);
    }
  };

  const fetchDashboardData = async (
    additionalParams = {},
    isFilterApply = false
  ) => {
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    try {
      // Build params object with proper filter handling
      const params = {
        type: activeSubTab,
        ...additionalParams,
      };

      if (isFilterApply) {
        if (filters.district_id && filters.district_id !== "") {
          params.district_id = filters.district_id;
        }

        if (filters.cycle_id && filters.cycle_id !== "") {
          params.cycle_id = filters.cycle_id;
        }

        // KPI-specific filters
        if (filters.qualification_status && filters.qualification_status !== "") {
          params.qualification_status = filters.qualification_status;
        }

        if (filters.overall_status && filters.overall_status !== "") {
          params.overall_status = filters.overall_status;
        }

        if (filters.business_entity && filters.business_entity !== "") {
          params.business_entity = filters.business_entity;
        }

        if (filters.gender && filters.gender !== "") {
          params.gender = filters.gender;
        }

        if (filters.business_sector && filters.business_sector !== "") {
          params.business_sector = filters.business_sector;
        }

        if (filters.ips_group && filters.ips_group !== "") {
          params.ips_group = filters.ips_group;
        }

        // Handle date filtering
        if (
          filters.dateField &&
          filters.condition &&
          filters.dateField !== "" &&
          filters.condition !== ""
        ) {
          params.date_field = filters.dateField;
          params.condition = filters.condition;

          if (filters.condition === "between") {
            if (
              filters.startDate &&
              filters.endDate &&
              filters.startDate !== "" &&
              filters.endDate !== ""
            ) {
              params.start_date = filters.startDate;
              params.end_date = filters.endDate;
            } else {
              delete params.date_field;
              delete params.condition;
            }
          } else {
            if (filters.dateValue && filters.dateValue !== "") {
              params.date_value = filters.dateValue;
            } else {
              delete params.date_field;
              delete params.condition;
            }
          }
        }
      }

      // Clean empty parameters
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });

      const response = await fetchData(
        "business-grants/dashboard",
        "GET",
        null,
        {},
        params,
        navigate,
        "/business-grants",
        dispatch
      );
      if (requestId !== latestRequestRef.current) return;

      if (response.success) {
        setDashboardData(response.data);
        if (isFilterApply) {
          setFiltersApplied(true);
        }
      } else {
        setDashboardData(createEmptyData());
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error("Error fetching dashboard data:", error);
      setDashboardData(createEmptyData());
    }
    setLoading(false);
  };

  const createEmptyData = () => {
    return {
      total: 0,
      qualified: 0,
      notQualified: 0,
      pending: 0,
      businessTypeData: [],
      tradeBusinessData: [],
      genderData: [],
      districtData: {},
      statusData: { completed: 0, inProgress: 0, delayed: 0, postponed: 0 },
      totalAmountPkr: 0,
      totalAmountEuro: 0,
      expectedAmountPkr: 0,
      disbursedAmount: 0,
      remainingAmount: 0,
      businessTypeTotal: 0,
      tradeBusinessTotal: 0,
    };
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };

      // Clear dependent fields when changing key fields
      if (key === "dateField" && value === "") {
        newFilters.condition = "";
        newFilters.dateValue = "";
        newFilters.startDate = "";
        newFilters.endDate = "";
      } else if (key === "condition") {
        if (value !== "between") {
          newFilters.startDate = "";
          newFilters.endDate = "";
        } else {
          newFilters.dateValue = "";
        }
      }

      return newFilters;
    });

    if (filtersApplied) {
      setFiltersApplied(false);
    }
  };

  const [activeChips, setActiveChips] = useState([]);
  const [refetchTick, setRefetchTick] = useState(0);

  const buildChips = (f) => {
    const labelOf = (options, value) =>
      (options || []).find((o) => String(o.value) === String(value))?.label || value;
    const fields = [
      ["ips_group", "IPS", ipsGroups],
      ["cycle_id", "Cycle", filtersData.cycles],
      ["district_id", "District", filtersData.districts],
      ["qualification_status", "Qualification Status", filtersData.qualificationStatuses],
      ["overall_status", "Overall Status", filtersData.overallStatuses],
      ["business_entity", "Business Entity", filtersData.businessEntities],
      ["gender", "Gender", filtersData.genders],
      ["business_sector", "Business Sector", filtersData.businessSectors],
    ];
    const chips = fields
      .filter(([key]) => f[key])
      .map(([key, label, options]) => ({ key, label, value: labelOf(options, f[key]) }));
    const hasDate =
      f.dateField &&
      f.condition &&
      (f.condition === "between" ? f.startDate && f.endDate : f.dateValue);
    if (hasDate) {
      chips.push({
        key: "date",
        label: labelOf(currentDateFields, f.dateField),
        value:
          f.condition === "between"
            ? `Between ${f.startDate} – ${f.endDate}`
            : `${labelOf(filtersData.conditions, f.condition)} ${f.dateValue}`,
      });
    }
    return chips;
  };

  const removeFilter = (key) => {
    const keys =
      key === "date" ? ["dateField", "condition", "dateValue", "startDate", "endDate"] : [key];
    const updated = { ...filters };
    keys.forEach((k) => {
      updated[k] = "";
    });
    const chips = buildChips(updated);
    if (!chips.length) {
      resetFilters();
      return;
    }
    setFilters(updated);
    setActiveChips(chips);
    setRefetchTick((t) => t + 1);
  };

  useEffect(() => {
    if (refetchTick > 0) {
      setDashboardData(null);
      fetchDashboardData({}, true);
    }
  }, [refetchTick]);

  const applyFilters = () => {
    // Validation logic
    if (filters.condition && !filters.dateField) {
      toast.error("Please select a date field when using a condition");
      return;
    }

    if (filters.condition === "between") {
      if (!filters.startDate || !filters.endDate) {
        toast.error(
          "Please provide both start and end dates for 'between' condition"
        );
        return;
      }
    }

    if (
      filters.condition &&
      filters.condition !== "between" &&
      !filters.dateValue
    ) {
      toast.error("Please provide a date value for the selected condition");
      return;
    }

    setActiveChips(buildChips(filters));
    setDashboardData(null);
    fetchDashboardData({}, true);
  };

  const resetFilters = () => {
    setActiveChips([]);
    setFilters({
      district_id: "",
      cycle_id: "",
      dateField: "",
      condition: "",
      dateValue: "",
      startDate: "",
      endDate: "",
      qualification_status: "",
      overall_status: "",
      business_entity: "",
      gender: "",
      business_sector: "",
      ips_group: "",
    });
    setFiltersApplied(false);
    setDashboardData(null);
    fetchDashboardData({}, false);
  };

  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };

  const handleTabChange = (tab) => {
    setActiveSubTab(tab);
    setActiveChips([]);
    // Clear filters when changing tabs to avoid confusion
    setFilters({
      district_id: "",
      cycle_id: "",
      dateField: "",
      condition: "",
      dateValue: "",
      startDate: "",
      endDate: "",
      qualification_status: "",
      overall_status: "",
      business_entity: "",
      gender: "",
      business_sector: "",
      ips_group: "",
    });
    setFiltersApplied(false);
  };

  const currentDateFields = ["sme", "corporate", "social-enterprise", "sedf-enterprise"].includes(
    activeSubTab
  )
    ? filtersData.smeDateFields
    : filtersData.microDateFields;

  const d = dashboardData || {};
  const has = {
    summary: hasData(d.total, d.totalAmountPkr, d.expectedAmountPkr, d.statusData),
    breakdowns: hasData(
      (d.businessTypeData || []).map((i) => i.value),
      (d.genderData || []).map((i) => i.value),
      (d.tradeBusinessData || []).map((i) => i.value)
    ),
    districts: Object.keys(d.districtData || {}).length > 0,
    milestones: (d.milestoneStatusData || []).length > 0,
  };
  const nothingToShow = !has.summary && !has.breakdowns && !has.districts && !has.milestones;

  return (
    <div className="my-4 w-full px-4 bg-gray-50">
      {/* Sub Tabs */}
      <div className="flex border-b border-gray-200 mb-4 bg-white rounded-lg shadow-sm">
        {canViewSME && (
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-all duration-200 ${
              activeSubTab === "sme"
                ? "text-[#137e81] border-[#137e81]"
                : "text-gray-600 border-transparent hover:border-[#137e81]"
            }`}
            onClick={() => handleTabChange("sme")}
          >
            SME Grant
          </button>
        )}
        {canViewCorporate && (
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-all duration-200 ${
              activeSubTab === "corporate"
                ? "text-[#137e81] border-[#137e81]"
                : "text-gray-600 border-transparent hover:border-[#137e81]"
            }`}
            onClick={() => handleTabChange("corporate")}
          >
            Corporate Grant
          </button>
        )}
        {canViewSocialEnterprise && (
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-all duration-200 ${
              activeSubTab === "social-enterprise"
                ? "text-[#137e81] border-[#137e81]"
                : "text-gray-600 border-transparent hover:border-[#137e81]"
            }`}
            onClick={() => handleTabChange("social-enterprise")}
          >
            Social Enterprise Grant
          </button>
        )}
        {canViewSedfEnterprise && (
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-all duration-200 ${
              activeSubTab === "sedf-enterprise"
                ? "text-[#137e81] border-[#137e81]"
                : "text-gray-600 border-transparent hover:border-[#137e81]"
            }`}
            onClick={() => handleTabChange("sedf-enterprise")}
          >
            SEDF Enterprise Grant
          </button>
        )}
        {canViewMicro && (
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-all duration-200 ${
              activeSubTab === "micro"
                ? "text-[#137e81] border-[#137e81]"
                : "text-gray-600 border-transparent hover:border-[#137e81]"
            }`}
            onClick={() => handleTabChange("micro")}
          >
            Micro Grant
          </button>
        )}
      </div>

      {/* Filters - Matching original design */}
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <Accordion>
          <AccordionSummary
            expandIcon={
              isFiltersVisible ? (
                <Minus color="#ffffff" size={14} />
              ) : (
                <Plus size={14} color="#ffffff" />
              )
            }
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{
              backgroundColor: "#137e81",
              color: "#fff !important",
              "& .MuiAccordionSummary-content": {
                color: "#fff !important",
              },
            }}
            onClick={toggleFilters}
          >
            Filters
          </AccordionSummary>
          <AccordionDetails>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
              {isAdmin && activeSubTab === "micro" && (
                <ThinDropdownWithSearch required={false}
                  label="IPS"
                  id="ips-dropdown"
                  options={ipsGroups}
                  value={filters.ips_group}
                  onChange={(value) => handleFilterChange("ips_group", value)}
                />
              )}

              <ThinDropdownWithSearch required={false}
                label="Cycle"
                id="cycle-dropdown"
                options={filtersData.cycles}
                value={filters.cycle_id}
                onChange={(value) => handleFilterChange("cycle_id", value)}
              />

              <ThinDropdownWithSearch required={false}
                label="District"
                id="district-dropdown"
                options={filtersData.districts}
                value={filters.district_id}
                onChange={(value) => handleFilterChange("district_id", value)}
              />

              <ThinDropdownWithSearch required={false}
                label="Qualification Status"
                id="qualification-status-dropdown"
                options={filtersData.qualificationStatuses || [
                  { label: "Qualified", value: "qualified" },
                  { label: "Not Qualified", value: "not_qualified" },
                  { label: "Pending", value: "pending" },
                ]}
                value={filters.qualification_status}
                onChange={(value) => handleFilterChange("qualification_status", value)}
              />

              <ThinDropdownWithSearch required={false}
                label="Overall Status"
                id="overall-status-dropdown"
                options={filtersData.overallStatuses || [
                  { label: "Completed", value: "Completed" },
                  { label: "In Progress", value: "In progress" },
                  { label: "Delayed", value: "Delayed" },
                  { label: "Postponed", value: "Postponed" },
                  { label: "Cancelled", value: "Cancelled" },
                ]}
                value={filters.overall_status}
                onChange={(value) => handleFilterChange("overall_status", value)}
              />

              <ThinDropdownWithSearch required={false}
                label="Business Entity"
                id="business-entity-dropdown"
                options={filtersData.businessEntities || [
                  { label: "Partnership", value: "Partnership" },
                  { label: "Private Ltd", value: "Private Ltd" },
                  { label: "Sole Proprietor", value: "Sole Proprietor" },
                  { label: "Association", value: "Association" },
                  { label: "Other/Specify", value: "Other/Specify" },
                ]}
                value={filters.business_entity}
                onChange={(value) => handleFilterChange("business_entity", value)}
              />

              <ThinDropdownWithSearch required={false}
                label="Gender"
                id="gender-dropdown"
                options={filtersData.genders || [
                  { label: "Male", value: "Male" },
                  { label: "Female", value: "Female" },
                  { label: "Transgender", value: "Transgender" },
                  { label: "Other", value: "Other" },
                ]}
                value={filters.gender}
                onChange={(value) => handleFilterChange("gender", value)}
              />

              <ThinDropdownWithSearch required={false}
                label="Business Sector"
                id="business-sector-dropdown"
                options={filtersData.businessSectors || [
                  { label: "Aquaculture & Fisheries", value: "Aquaculture & Fisheries" },
                  { label: "Agri-Food & Beverage Processing", value: "Agri-Food & Beverage Processing" },
                  { label: "Livestock, Poultry & Meat Value Chain", value: "Livestock, Poultry & Meat Value Chain" },
                  { label: "Textiles, Apparel & Handicrafts", value: "Textiles, Apparel & Handicrafts" },
                  { label: "Automotive & Light Engineering", value: "Automotive & Light Engineering" },
                  { label: "Construction Materials & Prefabrication", value: "Construction Materials & Prefabrication" },
                  { label: "Renewable Energy & Energy Services", value: "Renewable Energy & Energy Services" },
                  { label: "Health & Care Services", value: "Health & Care Services" },
                  { label: "Education, Training & EdTech", value: "Education, Training & EdTech" },
                  { label: "Tourism, Hospitality & Creative Services", value: "Tourism, Hospitality & Creative Services" },
                  { label: "ICT & Digital Services", value: "ICT & Digital Services" },
                  { label: "Waste, Recycling & Circular Economy", value: "Waste, Recycling & Circular Economy" },
                  { label: "Retail, Distribution & Service Trades", value: "Retail, Distribution & Service Trades" },
                  { label: "Other/Specify", value: "Other/Specify" },
                ]}
                value={filters.business_sector}
                onChange={(value) => handleFilterChange("business_sector", value)}
              />

              <ThinDropdownWithSearch required={false}
                label="Date Field"
                id="date-field-dropdown"
                options={currentDateFields}
                value={filters.dateField}
                onChange={(value) => handleFilterChange("dateField", value)}
              />

              <ThinDropdownWithSearch required={false}
                label="Condition"
                id="condition-dropdown"
                options={filtersData.conditions}
                value={filters.condition}
                onChange={(value) => handleFilterChange("condition", value)}
                disabled={!filters.dateField}
              />

              {filters.condition === "between" ? (
                <>
                  <ThinTextField isRequired={false}
                    label="Start Date"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) =>
                      handleFilterChange("startDate", e.target.value)
                    }
                  />
                  <ThinTextField isRequired={false}
                    label="End Date"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) =>
                      handleFilterChange("endDate", e.target.value)
                    }
                  />
                </>
              ) : (
                <ThinTextField isRequired={false}
                  label="Date Value"
                  type="date"
                  value={filters.dateValue}
                  onChange={(e) =>
                    handleFilterChange("dateValue", e.target.value)
                  }
                  disabled={
                    !filters.condition || filters.condition === "between"
                  }
                />
              )}

              <div className="flex items-end gap-2">
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-[#137e81] text-white rounded-lg transition-colors"
                >
                  Apply
                </button>
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 bg-[#be9609] text-white rounded-lg hover:bg-[#a68108] transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      
        <ActiveFilterChips chips={activeChips} onRemove={removeFilter} onClearAll={resetFilters} />
        <br />
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
          <p className="mt-2 text-gray-600">
            Loading{" "}
            {
              {
                sme: "SME",
                corporate: "Corporate",
                "social-enterprise": "Social Enterprise",
                "sedf-enterprise": "SEDF Enterprise",
                micro: "Micro",
              }[activeSubTab]
            }{" "}
            grant data...
          </p>
          
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white border-dashed border-2 border-teal-700 min-h-40 w-full px-5">
        {(!dashboardData || nothingToShow) && !loading ? (
          <NoData className="py-16" />
        ) : (
          <div className="space-y-0">
            {has.summary && (
            <div>
           <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <ApplicationsSection currentData={dashboardData} />
            <TotalAllocatedSection
              activeSubTab={activeSubTab}
              currentData={dashboardData}
            />
            <StatusSection currentData={dashboardData} />
          </div>
        </div>
            )}

  {/* Row 2 */}
  {has.breakdowns && (<>
  <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div>
        <BusinessTypeSection currentData={dashboardData} />
      </div>
      <div>
        <GenderSection currentData={dashboardData} />
      </div>
    </div>

    <hr className="border-t-2 border-dashed border-teal-700" />
    <BusinessSectorSection currentData={dashboardData} />
  </>)}

            {/* Third Row - District summary table */}
            {has.districts && (<>
            <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SummaryTableSection currentData={dashboardData} />

            </div>
            </>)}

            {/* Fourth Row - Milestone Status */}
            {has.milestones && (<>
            <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
            <div className="grid grid-cols-1 gap-4">
              <MilestoneStatusSection currentData={dashboardData} />
            </div>
            </>)}
          </div>
        )}
       
      </div>
    </div>
  );
};

export default BusinessGrantsDashboard;
